import { z } from 'zod';
// ============================================================================
// Users
// ============================================================================
export const UserRoleSchema = z.enum(['admin', 'member', 'viewer']);
export const UserStatusSchema = z.enum(['invited', 'active', 'suspended', 'deactivated']);
export const UserSchema = z.object({
    id: z.string().regex(/^usr_[A-Za-z0-9_-]+$/),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    email: z.string().email(),
    name: z.string().optional(),
    role: UserRoleSchema,
    status: UserStatusSchema,
    external_id: z.string().nullable(),
    created_at: z.string().datetime(),
    last_active_at: z.string().datetime().nullable(),
    /** Google `sub` claim if the user has linked Sign in with Google. */
    google_sub: z.string().nullable().optional(),
});
export const UserCreateSchema = z.object({
    email: z.string().email(),
    name: z.string().min(1).max(120).optional(),
    role: UserRoleSchema,
    external_id: z.string().min(1).max(256).nullable().optional(),
});
export const UserUpdateSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    role: UserRoleSchema.optional(),
    /** Only `active` and `suspended` are toggleable; deactivation is via DELETE. */
    status: z.enum(['active', 'suspended']).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field required' });
// ============================================================================
// User linkage (separate-but-linked identity per Decision #9)
// ============================================================================
/**
 * Maps an x-streamConnect user to their counterpart in an external system
 * (the user's other application, or a 3rd-party partner). One user can have
 * multiple linkages (one per external system).
 */
export const UserLinkageSchema = z.object({
    user_id: z.string().regex(/^usr_[A-Za-z0-9_-]+$/),
    external_system: z.string().min(1).max(64),
    external_id: z.string().min(1).max(256),
    linked_at: z.string().datetime(),
    metadata: z.record(z.unknown()).optional(),
});
// ============================================================================
// OAuth scopes
// ============================================================================
export const ScopeSchema = z.enum([
    'read:events',
    'write:events',
    'read:campaigns',
    'write:campaigns',
    'read:contacts',
    'write:contacts',
    'read:templates',
    'write:templates',
    'admin',
]);
/** Parses a space-separated scope string (per RFC 6749 §3.3). */
export function parseScopeString(input) {
    if (!input)
        return [];
    const parts = input.split(/\s+/).filter(Boolean);
    const result = [];
    for (const part of parts) {
        const parsed = ScopeSchema.safeParse(part);
        if (parsed.success)
            result.push(parsed.data);
    }
    return result;
}
export function formatScopeString(scopes) {
    return scopes.join(' ');
}
// ============================================================================
// OAuth clients (for partners + the user's other app)
// ============================================================================
export const OAuthClientTypeSchema = z.enum(['confidential', 'public']);
export const OAuthGrantTypeSchema = z.enum([
    'client_credentials',
    'authorization_code',
    'refresh_token',
]);
export const OAuthClientSchema = z.object({
    id: z.string().regex(/^client_[A-Za-z0-9_-]+$/),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    name: z.string().min(1).max(120),
    type: OAuthClientTypeSchema,
    client_id: z.string().min(8),
    /** Argon2 or bcrypt hash. Never expose the plaintext secret after creation. */
    client_secret_hash: z.string(),
    redirect_uris: z.array(z.string().url()),
    allowed_grants: z.array(OAuthGrantTypeSchema).min(1),
    allowed_scopes: z.array(ScopeSchema),
    active: z.boolean(),
    created_at: z.string().datetime(),
});
// ============================================================================
// OAuth requests / responses (RFC 6749)
// ============================================================================
export const TokenRequestSchema = z
    .object({
    grant_type: OAuthGrantTypeSchema,
    client_id: z.string().optional(),
    client_secret: z.string().optional(),
    scope: z.string().optional(),
    code: z.string().optional(),
    redirect_uri: z.string().url().optional(),
    refresh_token: z.string().optional(),
})
    .superRefine((value, ctx) => {
    if (value.grant_type === 'authorization_code' && !value.code) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['code'],
            message: 'code is required for authorization_code grant',
        });
    }
    if (value.grant_type === 'refresh_token' && !value.refresh_token) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['refresh_token'],
            message: 'refresh_token is required for refresh_token grant',
        });
    }
});
export const TokenResponseSchema = z.object({
    access_token: z.string(),
    token_type: z.literal('Bearer'),
    expires_in: z.number().int().positive(),
    refresh_token: z.string().optional(),
    scope: z.string().optional(),
});
// ============================================================================
// JWT access token payload
// ============================================================================
/**
 * The shape of a decoded access token JWT. Matches the public OpenAPI's
 * `oauth2` security scheme expectations.
 */
export const AccessTokenPayloadSchema = z.object({
    sub: z.string(),
    iss: z.string().url(),
    aud: z.union([z.string(), z.array(z.string())]),
    iat: z.number().int(),
    exp: z.number().int(),
    jti: z.string(),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    scope: z.string(),
    client_id: z.string().optional(),
    token_type: z.literal('access'),
});
//# sourceMappingURL=identity.js.map